import { create } from 'zustand'
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  User as FirebaseUser
} from 'firebase/auth'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { auth, db } from '../lib/firebase'
import { User } from '../types'

interface AuthState {
  user: User | null
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  checkAuth: () => () => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,

  login: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null })
      await signInWithEmailAndPassword(auth, email, password)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Login failed' })
    } finally {
      set({ loading: false })
    }
  },

  register: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null })
      const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password)
      
      // Create user document in Firestore
      const userData: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        createdAt: new Date().toISOString(),
      }
      
      await setDoc(doc(db, 'users', firebaseUser.uid), userData)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Registration failed' })
    } finally {
      set({ loading: false })
    }
  },

  logout: async () => {
    try {
      await signOut(auth)
      set({ user: null })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Logout failed' })
    }
  },

  resetPassword: async (email: string) => {
    try {
      set({ loading: true, error: null })
      await sendPasswordResetEmail(auth, email)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to send reset email' })
    } finally {
      set({ loading: false })
    }
  },

  checkAuth: () => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          // Get user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
          if (userDoc.exists()) {
            set({ user: userDoc.data() as User, loading: false })
          } else {
            // Create user document if it doesn't exist
            const userData: User = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              createdAt: new Date().toISOString(),
            }
            await setDoc(doc(db, 'users', firebaseUser.uid), userData)
            set({ user: userData, loading: false })
          }
        } catch (error) {
          console.error('Error fetching user data:', error)
          set({ user: null, loading: false })
        }
      } else {
        set({ user: null, loading: false })
      }
    })

    return unsubscribe
  },

  clearError: () => set({ error: null }),
})) 